// src/connectors/pages/ConnectorsPage.jsx
//
// Built entirely on the shared/ui kit (PageHeader, Tabs, Button, Alert,
// StatusBadge, Modal, EmptyState, Spinner) so this reads as part of the
// same app as the Users/Roles/Permissions admin pages, not a new
// bolted-on visual style. The tab strip carries a small per-family
// connection count (see `allConnections` below) so you can tell at a
// glance which categories have anything registered without clicking
// through each one — a standalone KPI summary row was tried here too but
// didn't earn its space, so it's gone.
//
// Four tabs — LLM, Source Control, Work Item, CRM — but LLM is backed by a
// genuinely different API/data shape (src/llmConnections/), since it
// supports four registration levels (org/account/project/personal) with
// different permission requirements per level, versus Source Control/Work
// Item's flat, per-user-owned model (each user only ever sees the
// connections *they* personally registered). The `activeFamily === "llm"`
// branches below dispatch to that module's api/card/modal instead of the
// generic ones; everything else (tabs, empty states, test-result session
// cache) is shared across all four.

import { useCallback, useEffect, useState } from "react";
import { Plus, Cable } from "lucide-react";
import { PageHeader, Button, EmptyState, Tabs, SkeletonCardGrid } from "../../shared/ui";
import { CONNECTOR_FAMILIES } from "../constants";
import * as connectorsApi from "../api/connectorsApi";
import * as llmConnectionsApi from "../../llmConnections/api/llmConnectionsApi";
import ConnectionCard from "../components/ConnectionCard";
import RegisterConnectionModal from "../components/RegisterConnectionModal";
import LlmConnectionCard from "../../llmConnections/components/LlmConnectionCard";
import RegisterLlmConnectionModal from "../../llmConnections/components/RegisterLlmConnectionModal";

const FAMILY_EMPTY_COPY = {
  llm: "Register an Ollama or Azure OpenAI connection so ADAI has a model to call.",
  source_control: "Register a GitHub, Bitbucket, Azure DevOps Repos, or ServiceNow connection to pull diffs from.",
  work_item: "Register a Jira or Azure DevOps Boards connection to sync work items from.",
  crm: "Not available yet — CRM connections aren't supported by the backend.",
};

function listConnectionsFor(family) {
  return family === "llm" ? llmConnectionsApi.listConnections() : connectorsApi.listConnections(family);
}

export default function ConnectorsPage() {
  const [activeFamily, setActiveFamily] = useState(CONNECTOR_FAMILIES[0].key);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  // The backend doesn't persist a connection's last-test result — keep the
  // most recent one here, per connection id, for this session only. Reset
  // whenever the active family changes so a stale result from another
  // family's connection id can't bleed through.
  const [testResults, setTestResults] = useState({});

  // Cross-family snapshot (all four families' lists) purely for the
  // per-tab counts — the card grid below still only fetches/shows the
  // active family, unchanged from before.
  const [allConnections, setAllConnections] = useState({});

  const refresh = useCallback(async (family) => {
    setLoading(true);
    setTestResults({});
    try {
      const data = await listConnectionsFor(family);
      setConnections(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    const entries = await Promise.all(
      CONNECTOR_FAMILIES.map(async (f) => [f.key, await listConnectionsFor(f.key)])
    );
    setAllConnections(Object.fromEntries(entries));
  }, []);

  useEffect(() => {
    refresh(activeFamily);
  }, [activeFamily, refresh]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  async function handleRegister(payload) {
    const result = activeFamily === "llm"
      ? await llmConnectionsApi.registerConnection(payload)
      : await connectorsApi.registerConnection(activeFamily, payload);
    await Promise.all([refresh(activeFamily), refreshSummary()]);
    return result;
  }

  async function handleTest(connection) {
    setTestingId(connection.id);
    try {
      const result = activeFamily === "llm"
        ? await llmConnectionsApi.testConnection(connection.id)
        : await connectorsApi.testConnection(activeFamily, connection.id);
      setTestResults((prev) => ({
        ...prev,
        [connection.id]: { ...result, testedAt: new Date().toISOString() },
      }));
    } finally {
      setTestingId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Connections"
        subtitle="Register and manage the external systems Jade Orion talks to on your behalf."
        actions={
          <Button variant="primary" onClick={() => setRegisterOpen(true)}>
            <Plus size={15} /> Register connection
          </Button>
        }
      />

      <Tabs
        tabs={CONNECTOR_FAMILIES.map((f) => {
          const isActiveTab = f.key === activeFamily;
          const isCrm = f.key === "crm";
          return {
            key: f.key,
            disabled: isCrm,
            title: isCrm ? "Not available yet" : undefined,
            label: (
              <>
                {f.label}
                {allConnections[f.key]?.length > 0 && (
                  <span
                    className={[
                      "ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[11px] font-semibold",
                      isActiveTab ? "bg-white/25 text-white" : "bg-gray-200 text-slate-600",
                    ].join(" ")}
                  >
                    {allConnections[f.key].length}
                  </span>
                )}
              </>
            ),
          };
        })}
        active={activeFamily}
        onChange={setActiveFamily}
        className="mb-5"
      />

      {loading ? (
        <SkeletonCardGrid cards={6} />
      ) : connections.length === 0 ? (
        <EmptyState
          icon={Cable}
          title={`No ${CONNECTOR_FAMILIES.find((f) => f.key === activeFamily)?.label.toLowerCase()} connections yet`}
          description={FAMILY_EMPTY_COPY[activeFamily]}
          action={
            activeFamily === "crm" ? undefined : (
              <Button variant="secondary" onClick={() => setRegisterOpen(true)}>
                <Plus size={15} /> Register connection
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {connections.map((c) => {
            const testResult = testResults[c.id];
            const connection = testResult
              ? { ...c, status: testResult.status, lastTestedAt: testResult.testedAt, lastError: testResult.status === "failed" ? testResult.message : undefined }
              : c;
            return activeFamily === "llm" ? (
              <LlmConnectionCard key={c.id} connection={connection} onTest={handleTest} testing={testingId === c.id} />
            ) : (
              <ConnectionCard
                key={c.id}
                family={activeFamily}
                connection={connection}
                onTest={handleTest}
                testing={testingId === c.id}
              />
            );
          })}
        </div>
      )}

      {activeFamily === "llm" ? (
        <RegisterLlmConnectionModal
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          onRegister={handleRegister}
        />
      ) : (
        <RegisterConnectionModal
          open={registerOpen}
          family={activeFamily}
          onClose={() => setRegisterOpen(false)}
          onRegister={handleRegister}
        />
      )}
    </div>
  );
}
